import { useQuery } from "@tanstack/react-query";
import {
  listPartners,
  listBranches,
  listVacatures,
  getVacatureActivity,
  getKamDashboard,
  getAdminDashboard,
  getHmpDashboard,
  getPartnerAdminDashboard,
  listUsers,
  listKams,
  listOrgContacts,
  listNotifications,
  getUnreadCount,
  listTasks,
  listAudit,
  listMyBranchRequests,
  listUserBranchRequests,
  getUserDepartmentMappings,
  suggestDepartments,
  getBranchAccessRequest,
  listPendingBranchAccessRequests,
  getSystemStatus,
  listEmailJobs,
  listSyncSheetsLogs,
  getSyncOverview,
  listSyncIssues,
  type SyncSheetsLogFilters,
  listApplicableFunctionTitleSuggestions,
  getProfile,
  resolveChat,
  listChats,
  listChatMessages,
  getUnreadChatCount,
} from "../api/api";
import { RequestOpts } from "../api/client";

export function usePartners(enabled = true, opts?: RequestOpts) {
  return useQuery({
    queryKey: ["partners"],
    queryFn: async () => (await listPartners(opts)).data,
    enabled,
  });
}

export function useBranches(partnerId?: number, enabled = true) {
  return useQuery({
    queryKey: ["branches", partnerId ?? null],
    queryFn: () => listBranches(partnerId),
    enabled,
  });
}

export function useMyBranchRequests(enabled = true) {
  return useQuery({
    queryKey: ["branch-requests", "me"],
    queryFn: listMyBranchRequests,
    enabled,
  });
}

export function useUserBranchRequests(userId: number | undefined) {
  return useQuery({
    queryKey: ["branch-requests", userId ?? null],
    queryFn: () => listUserBranchRequests(userId!),
    enabled: userId != null,
  });
}

export function useUserDepartmentMappings(userId: number | undefined) {
  return useQuery({
    queryKey: ["department-mappings", userId ?? null],
    queryFn: async () => (await getUserDepartmentMappings(userId!)).mappings,
    enabled: userId != null,
  });
}

// Suggested departments for a new vacature in branchId, from the
// contactpersoon's (or else the caller's) department mappings. Errors are
// silenced: a missing suggestion just means no prefill.
export function useDepartmentSuggestions(branchId: number | undefined, contactpersoon: string, enabled = true) {
  return useQuery({
    queryKey: ["department-suggestions", branchId ?? null, contactpersoon],
    queryFn: () => suggestDepartments(branchId!, contactpersoon || undefined, { silenceErrors: true }),
    enabled: enabled && branchId != null,
    staleTime: 60_000,
  });
}

export function useBranchAccessRequestDetail(requestId: number | undefined) {
  return useQuery({
    queryKey: ["branch-requests", "detail", requestId ?? null],
    queryFn: () => getBranchAccessRequest(requestId!),
    enabled: requestId != null,
  });
}

export function usePendingBranchAccessRequests(enabled = true) {
  return useQuery({
    queryKey: ["branch-requests", "pending"],
    queryFn: () => listPendingBranchAccessRequests(),
    enabled,
  });
}

export function useVacatures(
  params: Parameters<typeof listVacatures>[0] = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ["vacatures", params],
    queryFn: () => listVacatures(params),
    enabled,
  });
}

export function useVacatureActivity(id: number | undefined) {
  return useQuery({
    queryKey: ["vacature-activity", id],
    queryFn: () => getVacatureActivity(id!),
    enabled: id != null,
  });
}

export function useKamDashboard(isAdmin: boolean) {
  return useQuery({
    queryKey: ["dashboard", isAdmin ? "admin" : "kam"],
    queryFn: () => (isAdmin ? getAdminDashboard() : getKamDashboard()),
  });
}

export function useHmpDashboard(partnerId: number) {
  return useQuery({
    queryKey: ["dashboard", "hmp", partnerId],
    queryFn: () => getHmpDashboard(partnerId),
    enabled: partnerId > 0,
  });
}

export function usePartnerAdminDashboard(opts?: RequestOpts) {
  return useQuery({
    queryKey: ["dashboard", "partner-admin"],
    queryFn: () => getPartnerAdminDashboard(opts),
  });
}

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: ["users"],
    queryFn: async () => (await listUsers()).data,
    enabled,
  });
}

export function useProfile(enabled = true) {
  return useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    enabled,
  });
}

export function useKams(enabled = true) {
  return useQuery({
    queryKey: ["kams"],
    queryFn: async () => (await listKams()).data,
    enabled,
  });
}

export function useOrgContacts(enabled = true, partnerId?: number) {
  return useQuery({
    queryKey: ["org-contacts", partnerId ?? null],
    queryFn: async () => (await listOrgContacts(partnerId)).data,
    enabled,
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await listNotifications()).data,
    refetchInterval: 60_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
  });
}

export function useTasks() {
  return useQuery({
    queryKey: ["tasks"],
    queryFn: listTasks,
    refetchInterval: 60_000,
  });
}

export function useChatList(enabled = true) {
  return useQuery({
    queryKey: ["chats"],
    queryFn: listChats,
    refetchInterval: 6_000,
    enabled,
  });
}

export function useChatMessages(chatId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ["chat-messages", chatId ?? null],
    queryFn: () => listChatMessages(chatId!),
    refetchInterval: 6_000,
    enabled: enabled && chatId != null,
  });
}

export function useUnreadChatCount(enabled = true) {
  return useQuery({
    queryKey: ["chats", "unread-count"],
    queryFn: getUnreadChatCount,
    refetchInterval: 60_000,
    enabled,
  });
}

export function useAudit(page: number, limit: number, filters?: { actorEmail?: string; actorDomain?: string; partnerId?: number }) {
  return useQuery({
    queryKey: ["audit", page, limit, filters?.actorEmail ?? null, filters?.actorDomain ?? null, filters?.partnerId ?? null],
    queryFn: () => listAudit(page, limit, filters),
  });
}

export function useSystemStatus(opts?: RequestOpts) {
  return useQuery({
    queryKey: ["admin", "system-status"],
    queryFn: () => getSystemStatus(opts),
    refetchOnWindowFocus: false,
  });
}

export function useEmailJobs(
  page: number,
  limit: number,
  filters?: { recipientEmail?: string; emailType?: string; status?: string }
) {
  return useQuery({
    queryKey: ["admin", "emails", page, limit, filters?.recipientEmail ?? null, filters?.emailType ?? null, filters?.status ?? null],
    queryFn: () => listEmailJobs(page, limit, filters),
  });
}

export function useSyncSheetsLogs(page: number, limit: number, filters?: SyncSheetsLogFilters) {
  return useQuery({
    queryKey: ["admin", "sync-logs", page, limit, filters ?? null],
    queryFn: () => listSyncSheetsLogs(page, limit, filters),
  });
}

export function useSyncOverview() {
  return useQuery({
    queryKey: ["admin", "sync-logs", "overview"],
    queryFn: () => getSyncOverview(),
    refetchInterval: 60_000,
  });
}

export function useSyncIssues(partnerId?: number, days = 7) {
  return useQuery({
    queryKey: ["admin", "sync-logs", "issues", partnerId ?? null, days],
    queryFn: () => listSyncIssues(partnerId, days),
  });
}

export function useApplicableFunctionTitleSuggestions(partnerId?: number, enabled = true) {
  return useQuery({
    queryKey: ["function-title-suggestions", "applicable", partnerId ?? null],
    queryFn: () => listApplicableFunctionTitleSuggestions(partnerId),
    enabled,
  });
}
